import { useTranslation } from "react-i18next";
import { useState } from "react";
import {
  buildNotesTableDataSource,
  useDefaultFilterMenuOptions,
  useNotesTableColumns,
} from "@/entities/notes-table-v3";
import { compareDatesByDay, formatDayJs, formatStringToDayJs, isSubstringOf } from "@/shared/utils";
import { useAppDispatch, useAppSelector } from "@/services/store";
import { DEFAULT_NOTE_TYPES, useGetAllNotesQuery, useGetAllPersonsQuery } from "@/services/api";
import { useNotesRedirect } from "@/features/notes-manager";
import type { NotesTableRowData } from "@/entities/notes-table-v3";
import type { Widget, WidgetContentProps } from "../types";
import { allWidgetsSelector, setAllWidgets } from "../slice";

export const useDashboardsCommitmentsTable = ({
  widgetId,
  headerDirectionFilter,
}: {
  widgetId?: string;
  headerDirectionFilter?: WidgetContentProps["directionFilter"];
}) => {
  const { t } = useTranslation();
  const { notesTableColumnsGetters } = useNotesTableColumns();
  const dispatch = useAppDispatch();
  const widgets = useAppSelector(allWidgetsSelector);

  const [titleFilter, setTitleFilter] = useState<string>("");
  const [noteTitleFilter, setNoteTitleFilter] = useState<string>("");

  const { getDefaultDirectionOptions, getDefaultNoteStatusOptions, getDefaultPersonGroupOptions } =
    useDefaultFilterMenuOptions();

  const currentWidget = widgets.find((widget) => widget.id === widgetId);

  const excludedCurrentWidgetWidgets = widgets.filter((widget) => widget.id !== currentWidget?.id);

  const setAllStateWidgets = (allWidgets: Widget[]) => {
    dispatch(setAllWidgets(allWidgets));
  };

  const { data: notes, isLoading: notesIsLoading } = useGetAllNotesQuery();

  const { data: persons, isLoading: personsIsLoading } = useGetAllPersonsQuery();

  const isLoading = notesIsLoading || personsIsLoading;

  const { redirect } = useNotesRedirect();

  const onRowClick = (row: NotesTableRowData) => {
    redirect({ note: row });
  };

  const setFromDateFilter = (value: string) => {
    if (!currentWidget) return;
    setAllStateWidgets([
      ...excludedCurrentWidgetWidgets,
      { ...currentWidget, notesFilters: { ...currentWidget.notesFilters, fromDateFilter: value } },
    ]);
  };

  const setToDateFilter = (value: string) => {
    if (!currentWidget) return;
    setAllStateWidgets([
      ...excludedCurrentWidgetWidgets,
      { ...currentWidget, notesFilters: { ...currentWidget.notesFilters, toDateFilter: value } },
    ]);
  };

  const setToDueDate = (value: string) => {
    if (!currentWidget) return;
    setAllStateWidgets([
      ...excludedCurrentWidgetWidgets,
      { ...currentWidget, notesFilters: { ...currentWidget.notesFilters, toDueDateFilter: value } },
    ]);
  };

  const setFromDueDate = (value: string) => {
    if (!currentWidget) return;
    setAllStateWidgets([
      ...excludedCurrentWidgetWidgets,
      {
        ...currentWidget,
        notesFilters: { ...currentWidget.notesFilters, fromDueDateFilter: value },
      },
    ]);
  };

  const setPersonGroupFilters = (value: string[]) => {
    if (!currentWidget) return;
    setAllStateWidgets([
      ...excludedCurrentWidgetWidgets,
      {
        ...currentWidget,
        notesFilters: { ...currentWidget.notesFilters, personGroupFilters: value },
      },
    ]);
  };

  const setDirectionFilters = (value: string[]) => {
    if (!currentWidget) return;
    setAllStateWidgets([
      ...excludedCurrentWidgetWidgets,
      {
        ...currentWidget,
        notesFilters: { ...currentWidget.notesFilters, directionFilters: value },
      },
    ]);
  };

  const setStatusFilters = (value: string[]) => {
    if (!currentWidget) return;
    setAllStateWidgets([
      ...excludedCurrentWidgetWidgets,
      {
        ...currentWidget,
        notesFilters: { ...currentWidget.notesFilters, statusFilters: value },
      },
    ]);
  };

  const personGroupFilters = currentWidget?.notesFilters?.personGroupFilters;
  const fromDateFilter = currentWidget?.notesFilters?.fromDateFilter;
  const toDateFilter = currentWidget?.notesFilters?.toDateFilter;
  const fromDueDate = currentWidget?.notesFilters?.fromDueDateFilter;
  const toDueDate = currentWidget?.notesFilters?.toDueDateFilter;
  const directionFilters = currentWidget?.notesFilters?.directionFilters;
  const statusFilters = currentWidget?.notesFilters?.statusFilters;

  let dataSource = buildNotesTableDataSource({
    notes:
      notes?.filter((note) => note.noteType.name === DEFAULT_NOTE_TYPES.DEFAULT_COMMITMENT) ?? [],
    persons: persons?.persons ?? [],
  });

  if (personGroupFilters?.length) {
    dataSource = dataSource.filter((record) => {
      const personGroup = record.person?.group;
      if (!personGroup) return false;
      return !!personGroupFilters.includes(personGroup.toUpperCase());
    });
  }

  if (titleFilter) {
    dataSource = dataSource.filter(({ person }) => {
      return !!isSubstringOf({
        sourceString: person?.title ?? "",
        searchString: titleFilter ?? "",
      });
    });
  }

  if (fromDateFilter) {
    dataSource = dataSource.filter((record) => {
      return !compareDatesByDay({
        firstDate: fromDateFilter,
        secondDate: record.noteDate,
      });
    });
  }

  if (toDateFilter) {
    dataSource = dataSource.filter((record) => {
      return !compareDatesByDay({
        firstDate: record.noteDate,
        secondDate: toDateFilter,
      });
    });
  }

  if (fromDueDate) {
    dataSource = dataSource.filter((record) => {
      return !compareDatesByDay({
        firstDate: fromDueDate,
        secondDate: record.dueDate,
      });
    });
  }

  if (toDueDate) {
    dataSource = dataSource.filter((record) => {
      return !compareDatesByDay({
        firstDate: record.dueDate,
        secondDate: toDueDate,
      });
    });
  }

  if (noteTitleFilter) {
    dataSource = dataSource.filter(({ title }) => {
      return !!isSubstringOf({
        sourceString: title ?? "",
        searchString: noteTitleFilter ?? "",
      });
    });
  }

  if (directionFilters?.length) {
    dataSource = dataSource.filter(({ direction }) => {
      if (!direction) return false;
      return !!directionFilters.includes(direction);
    });
  }

  if (headerDirectionFilter) {
    if (headerDirectionFilter !== "ALL") {
      dataSource = dataSource.filter(({ direction }) => {
        if (!direction) return false;
        return !!(headerDirectionFilter === direction);
      });
    }
  }

  if (statusFilters?.length) {
    dataSource = dataSource.filter(({ status }) => {
      if (!status) return false;
      return !!statusFilters.includes(status);
    });
  }

  const columns = [
    notesTableColumnsGetters.getNotesTableImportantColumn({
      title: "",
      dataIndex: ["important"],
    }),
    notesTableColumnsGetters.getNotesTableNoteDateColumn({
      title: t("common.date"),
      dataIndex: ["noteDate"],
      onChangeFilterDateFrom: (date) => setFromDateFilter(formatDayJs(date)),
      onChangeFilterDateTo: (date) => setToDateFilter(formatDayJs(date)),
      defaultValueFilterTo: toDateFilter ? formatStringToDayJs(toDateFilter) : undefined,
      defaultValueFilterFrom: fromDateFilter ? formatStringToDayJs(fromDateFilter) : undefined,
      colorize: true,
    }),
    notesTableColumnsGetters.getNotesTableDueDateColumn({
      title: t("notesPage.dueDate"),
      dataIndex: ["dueDate"],
      onChangeFilterDateFrom: (date) => setFromDueDate(formatDayJs(date)),
      onChangeFilterDateTo: (date) => setToDueDate(formatDayJs(date)),
      defaultValueFilterTo: toDueDate ? formatStringToDayJs(toDueDate) : undefined,
      defaultValueFilterFrom: fromDueDate ? formatStringToDayJs(fromDueDate) : undefined,
    }),
    notesTableColumnsGetters.getNotesTableDierctionColumn({
      title: "",
      dataIndex: ["direction"],
      menuFilters: getDefaultDirectionOptions().map((option) => ({
        ...option,
        defaultChecked: !!directionFilters?.find((item) => item === option.value),
      })),
      onChangeMenuFilters: (selected) => setDirectionFilters(selected),
    }),
    notesTableColumnsGetters.getNotesTablePersonColumn({
      title: t("common.person"),
      dataIndex: ["person"],
      menuFilters: getDefaultPersonGroupOptions().map((option) => ({
        ...option,
        defaultChecked: !!personGroupFilters?.find((item) => item === option.value),
      })),
      onChangeMenuFilters: (selected) => setPersonGroupFilters(selected),
      colorize: true,
    }),
    notesTableColumnsGetters.getNotesTablePersonTitleColumn({
      title: t("common.title"),
      dataIndex: ["person.title"],
      onChangeFilter: setTitleFilter,
      filterValue: titleFilter,
      colorize: true,
    }),
    notesTableColumnsGetters.getNotesTableNoteTitleColumn({
      dataIndex: ["title"],
      title: t("common.noteType.commitment_one"),
      onChangeFilter: setNoteTitleFilter,
      filterValue: noteTitleFilter,
      colorize: true,
    }),
    notesTableColumnsGetters.getNotesTableNoteStatusColumn({
      title: t("common.status.status"),
      dataIndex: ["status"],
      menuFilters: getDefaultNoteStatusOptions().map((option) => ({
        ...option,
        defaultChecked: !!statusFilters?.find((item) => item === option.value),
      })),
      onChangeMenuFilters: (selected: string[]) => setStatusFilters(selected),
    }),
  ];

  return { state: { columns, isLoading, dataSource }, methods: { onRowClick } };
};
